import type { Tone } from "../../shared/ui";
import type {
  ChangeApprovalStatus,
  ScheduleChangeReason,
  ScheduleChangeType,
  ScopeChangeKind,
} from "../../shared/types/api";

/**
 * §59's codes as people say them, plus the grouping that makes a list of sixteen usable.
 *
 * <p>The groups are not in the specification. Sixteen flat options in a dropdown is a list nobody
 * reads to the end of, and the person picking is usually deciding between "was this us or them"
 * first — so the list is arranged the way the choice is actually made.
 */
export const REASON_LABELS: Record<ScheduleChangeReason, string> = {
  SCOPE_CHANGE: "Scope changed",
  DEPENDENCY_DELAY: "Waiting on a dependency",
  RESOURCE_UNAVAILABLE: "Nobody available",
  RESOURCE_REALLOCATION: "People moved to other work",
  ESTIMATION_ERROR: "Estimate was wrong",
  TECHNICAL_COMPLEXITY: "Harder than expected",
  REWORK: "Rework",
  QA_REWORK: "Failed QA, reworked",
  CLIENT_DELAY: "Waiting on the client",
  CUSTOMER_CHANGE: "Customer changed their mind",
  MANAGEMENT_PRIORITY_CHANGE: "Priorities changed",
  PRODUCTION_INCIDENT: "Pulled onto a production incident",
  LEAVE: "Leave",
  ENVIRONMENT_UNAVAILABLE: "Environment unavailable",
  THIRD_PARTY_DEPENDENCY: "Third party",
  OTHER: "Other",
};

export const REASON_GROUPS: { label: string; codes: ScheduleChangeReason[] }[] = [
  {
    label: "Ours",
    codes: ["ESTIMATION_ERROR", "TECHNICAL_COMPLEXITY", "REWORK", "QA_REWORK", "SCOPE_CHANGE"],
  },
  {
    label: "People",
    codes: ["RESOURCE_UNAVAILABLE", "RESOURCE_REALLOCATION", "LEAVE", "PRODUCTION_INCIDENT"],
  },
  {
    label: "Outside the team",
    codes: [
      "CLIENT_DELAY",
      "CUSTOMER_CHANGE",
      "THIRD_PARTY_DEPENDENCY",
      "DEPENDENCY_DELAY",
      "ENVIRONMENT_UNAVAILABLE",
      "MANAGEMENT_PRIORITY_CHANGE",
    ],
  },
  { label: "Anything else", codes: ["OTHER"] },
];

/** Codes the backend counts as delay from outside the team. Kept in step with the enum. */
const EXTERNAL: ScheduleChangeReason[] = [
  "CLIENT_DELAY",
  "CUSTOMER_CHANGE",
  "THIRD_PARTY_DEPENDENCY",
  "ENVIRONMENT_UNAVAILABLE",
  "PRODUCTION_INCIDENT",
];

export function isExternal(reason: ScheduleChangeReason): boolean {
  return EXTERNAL.includes(reason);
}

export const CHANGE_TYPE_LABELS: Record<ScheduleChangeType, string> = {
  COMMITTED_START: "Committed start",
  COMMITTED_DUE: "Committed due",
  FORECAST_START: "Forecast start",
  FORECAST_DUE: "Forecast due",
};

export const APPROVAL_LABELS: Record<ChangeApprovalStatus, string> = {
  NOT_REQUIRED: "No sign-off needed",
  PENDING: "Waiting for sign-off",
  APPROVED: "Approved",
  REJECTED: "Declined",
};

export const APPROVAL_TONE: Record<ChangeApprovalStatus, Tone> = {
  NOT_REQUIRED: "gray",
  PENDING: "amber",
  APPROVED: "green",
  REJECTED: "red",
};

export const SCOPE_KIND_LABELS: Record<ScopeChangeKind, string> = {
  ADDED: "Added",
  REMOVED: "Removed",
  ESTIMATE_CHANGED: "Re-estimated",
};

export const SCOPE_KIND_TONE: Record<ScopeChangeKind, Tone> = {
  ADDED: "amber",
  REMOVED: "blue",
  ESTIMATE_CHANGED: "violet",
};

/**
 * Days, with the direction said rather than signed.
 *
 * <p>"−4 days" needs a moment's thought; "4 days earlier" does not, and the reader is usually
 * scanning a column of these looking for the bad ones.
 */
export function formatDays(days: number): string {
  if (days === 0) {
    return "no change";
  }
  const magnitude = Math.abs(days);
  const unit = magnitude === 1 ? "day" : "days";
  return days > 0 ? `${magnitude} ${unit} later` : `${magnitude} ${unit} earlier`;
}

/** Just the number and its sign, for totals where the column heading says what it is. */
export function formatSignedDays(days: number): string {
  return days > 0 ? `+${days}` : String(days);
}

/**
 * A share as a percentage.
 *
 * <p>A small but real share reads as "<1%" rather than rounding to "0%". Ten hours against a
 * five-thousand-hour commitment genuinely is under one percent, but "0%" beside "10h" reads as a
 * contradiction, and the reader is left deciding which of the two numbers to believe.
 */
export function formatPercent(share: number): string {
  const percent = share * 100;
  if (percent !== 0 && Math.abs(percent) < 0.5) {
    return percent > 0 ? "<1%" : ">-1%";
  }
  return `${Math.round(percent)}%`;
}
